import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { SelectField, TextAreaField } from '../../../components/ui/Field';
import { DataTable, ErrorState, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { http } from '../../../services/http';
import { formatDateTime } from '../../leads/api';

/** Phase 9 platform console additions: support access requests and aggregate analytics (12 §6.4). */
interface Grant {
  id: string;
  tenantId: string;
  reason: string;
  durationMinutes: number;
  status: 'requested' | 'approved' | 'rejected' | 'revoked' | 'expired';
  expiresAt: string | null;
  createdAt: string;
}

interface Analytics {
  tenantsByPlan: Record<string, number>;
  leads: { total: number; createdLast30Days: number };
  activeLast7Days: { users: number; tenants: number };
  topTenantsByNewLeads: { id: string; name: string; leadsLast30Days: number }[];
  pushDeliveriesLast7Days: Record<string, number>;
  customerMessagesLast7Days: { channel: string; status: string; count: number }[];
}

const grantsKey = ['platform', 'support-access'] as const;

export function SupportAccessCard({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const grants = useQuery({
    queryKey: grantsKey,
    queryFn: () => http.get<Grant[]>('/platform/support-access'),
  });
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('60');
  const [viewing, setViewing] = useState<string | null>(null);
  const request = useMutation({
    mutationFn: () =>
      http.post<Grant>(`/platform/tenants/${tenantId}/support-access`, {
        reason: reason.trim(),
        durationMinutes: Number(duration),
      }),
    onSuccess: async () => {
      setReason('');
      await queryClient.invalidateQueries({ queryKey: grantsKey });
      toast.success('Request sent. The business owner must approve it.');
    },
    onError: (err) => toast.error(err),
  });
  const mine = (grants.data ?? []).filter((g) => g.tenantId === tenantId);
  const active = mine.find((g) => g.status === 'approved');
  return (
    <Card>
      <CardHeader
        title="Support access"
        description="Read-only access to this business's data needs the owner's approval, ends on time, and every page you open is logged for them."
      />
      <CardBody className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <TextAreaField
            label="Reason (shown to the owner)"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <SelectField
            label="For"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            options={[
              { value: '30', label: '30 minutes' },
              { value: '60', label: '1 hour' },
              { value: '240', label: '4 hours' },
              { value: '1440', label: '24 hours' },
            ]}
          />
        </div>
        <Button
          loading={request.isPending}
          disabled={reason.trim().length < 10}
          onClick={() => request.mutate()}
        >
          Request access
        </Button>
        {grants.isPending ? <TableSkeleton rows={2} /> : null}
        {mine.length ? (
          <DataTable
            rows={mine}
            rowKey={(g) => g.id}
            empty={null}
            columns={[
              { header: 'Requested', cell: (g) => formatDateTime(g.createdAt) },
              { header: 'Reason', cell: (g) => g.reason },
              {
                header: 'Status',
                cell: (g) => (
                  <Badge
                    tone={
                      g.status === 'approved' ? 'success' : g.status === 'requested' ? 'warning' : 'neutral'
                    }
                  >
                    {g.status === 'approved' && g.expiresAt
                      ? `active until ${formatDateTime(g.expiresAt)}`
                      : g.status}
                  </Badge>
                ),
              },
            ]}
          />
        ) : null}
        {active ? (
          <Button variant="secondary" onClick={() => setViewing(viewing ? null : active.id)}>
            {viewing ? 'Hide leads' : 'View leads (read-only)'}
          </Button>
        ) : null}
        {viewing ? <SupportLeads grantId={viewing} /> : null}
      </CardBody>
    </Card>
  );
}

/** The tenant's lead list as support sees it: the normal API, with the grant header (audited server-side). */
function SupportLeads({ grantId }: { grantId: string }) {
  const leads = useQuery({
    queryKey: ['platform', 'support-leads', grantId],
    queryFn: () =>
      http.list<
        {
          id: string;
          leadNumber: string;
          fullName: string;
          stage: { name: string };
          owner: { name: string } | null;
          createdAt: string;
        }[]
      >('/leads', { query: { pageSize: 50 }, headers: { 'X-Support-Grant': grantId } }),
  });
  if (leads.isPending) return <TableSkeleton rows={3} />;
  if (leads.isError) return <ErrorState error={leads.error} />;
  return (
    <DataTable
      rows={leads.data.data}
      rowKey={(l) => l.id}
      empty={<p className="text-sm text-fg-subtle">No leads.</p>}
      columns={[
        { header: 'Lead', cell: (l) => l.leadNumber },
        { header: 'Name', cell: (l) => l.fullName },
        { header: 'Stage', cell: (l) => l.stage.name },
        { header: 'Owner', cell: (l) => l.owner?.name ?? '—' },
        { header: 'Created', cell: (l) => formatDateTime(l.createdAt) },
      ]}
    />
  );
}

export function PlatformAnalytics() {
  const data = useQuery({
    queryKey: ['platform', 'analytics'],
    queryFn: () => http.get<Analytics>('/platform/analytics'),
  });
  if (data.isPending) return <TableSkeleton rows={3} />;
  if (data.isError) return <ErrorState error={data.error} />;
  const a = data.data;
  const stat = (label: string, value: number | string) => (
    <Card key={label} className="p-5">
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      <p className="text-sm text-fg-muted">{label}</p>
    </Card>
  );
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stat('Leads (all businesses)', a.leads.total)}
        {stat('New leads, last 30 days', a.leads.createdLast30Days)}
        {stat('Active users, last 7 days', a.activeLast7Days.users)}
        {stat('Active businesses, last 7 days', a.activeLast7Days.tenants)}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Most new leads (30 days)" />
          <CardBody>
            <DataTable
              rows={a.topTenantsByNewLeads}
              rowKey={(t) => t.id}
              empty={<p className="text-sm text-fg-subtle">No leads yet.</p>}
              columns={[
                { header: 'Business', cell: (t) => t.name },
                { header: 'New leads', cell: (t) => t.leadsLast30Days, className: 'tabular-nums' },
              ]}
            />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Plans and messaging (7 days)" />
          <CardBody className="space-y-2 text-sm">
            <p>
              Plans:{' '}
              {Object.entries(a.tenantsByPlan)
                .map(([k, v]) => `${k} ${v}`)
                .join(' · ') || '—'}
            </p>
            <p>
              Push:{' '}
              {Object.entries(a.pushDeliveriesLast7Days)
                .map(([k, v]) => `${k} ${v}`)
                .join(' · ') || '—'}
            </p>
            <p>
              Customer messages:{' '}
              {a.customerMessagesLast7Days.map((m) => `${m.channel} ${m.status} ${m.count}`).join(' · ') ||
                '—'}
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
